import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageSquare, ArrowRight, Wind, CloudHail, TreePine, ShieldCheck, Award, Clock, FileCheck, Star, MapPin, Target, BadgeCheck } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { InspectionForm } from "@/components/site/InspectionForm";
import { GeorgiaMap } from "@/components/site/GeorgiaMap";
import { COUNTIES, PHONE, PHONE_TEL, TRUST_BADGES, SHINGLE_LINES, GOOGLE_REVIEW_URL } from "@/lib/constants";

const HERO_BG = "/storm-bg.png";

export default function Home() {
  useEffect(() => {
    if (window.location.hash === "#inspect") {
      document.getElementById("inspect")?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <>
      <Header />

      {/* HERO */}
      <section data-testid="hero" className="relative overflow-hidden border-b border-slate-900 min-h-[92vh] flex items-center">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/55 to-slate-950" />
        <div className="absolute inset-0 grain" />

        <div className="relative w-full max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center fade-up">
          {/* Top eyebrow with red rules */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="hidden sm:block h-px w-16 bg-red-600" />
            <div className="text-[11px] sm:text-sm uppercase tracking-[0.35em] text-white font-bold">
              Wind <span className="text-red-600">·</span> Hail <span className="text-red-600">·</span> Tree Damage Specialist
            </div>
            <span className="hidden sm:block h-px w-16 bg-red-600" />
          </div>

          {/* Massive headline */}
          <h1 className="font-impact uppercase leading-[0.92] text-white text-7xl sm:text-8xl lg:text-[9rem] tracking-tight drop-shadow-[0_8px_24px_rgba(0,0,0,0.6)]">
            Do you have <span className="text-red-600 text-grunge">Storm</span>
            <br />
            <span className="text-red-600 text-grunge">Damage</span>
            <span className="text-white">?</span>
          </h1>

          {/* Sub-headline */}
          <div className="mt-6 font-impact uppercase text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Get a{" "}
            <span className="relative inline-block text-red-600">
              Free
              <span className="absolute left-0 right-0 -bottom-1 h-1 bg-red-600" />
            </span>{" "}
            Roof Inspection.
          </div>

          {/* Body copy */}
          <div className="mt-12 space-y-3 max-w-3xl mx-auto">
            <p className="text-slate-200 text-base sm:text-lg">
              One missing or damaged shingle seen from the ground
            </p>
            <p className="font-impact uppercase text-white text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
              May qualify for a full <span className="text-red-600">roof replacement</span>
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>through your homeowner's insurance.
            </p>
            <p className="text-slate-300 text-sm sm:text-base pt-3">
              JD verifies claim eligibility — <span className="text-red-500">at no cost to you.</span>
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mt-10 justify-center items-stretch sm:items-center">
            <a
              data-testid="hero-call"
              href={`tel:${PHONE_TEL}`}
              className="bg-slate-950/70 backdrop-blur border-2 border-slate-700 text-white font-bold uppercase tracking-widest hover:border-red-600 hover:text-red-500 px-7 py-4 inline-flex items-center justify-center gap-3 transition-colors text-sm"
            >
              <Phone size={16} className="text-red-600" /> Call JD · {PHONE}
            </a>
            <a
              data-testid="hero-text"
              href={`sms:${PHONE_TEL}`}
              className="bg-slate-950/70 backdrop-blur border-2 border-slate-700 text-white font-bold uppercase tracking-widest hover:border-red-600 hover:text-red-500 px-7 py-4 inline-flex items-center justify-center gap-3 transition-colors text-sm"
            >
              <MessageSquare size={16} className="text-red-600" /> Text JD
            </a>
            <a
              data-testid="hero-scroll-form"
              href="#inspect"
              className="bg-slate-950/70 backdrop-blur border-2 border-slate-700 text-white font-bold uppercase tracking-widest hover:border-red-600 hover:text-red-500 px-7 py-4 inline-flex items-center justify-center gap-3 transition-colors text-sm"
            >
              Request Online <ArrowRight size={16} />
            </a>
          </div>

          {/* Stat strip */}
          <div className="mt-14 bg-slate-950/80 backdrop-blur border border-slate-800 grid grid-cols-2 md:grid-cols-4 max-w-5xl mx-auto">
            {[
              { Icon: ShieldCheck, k: "$0", v: "Inspection Cost", sub: "100% Free" },
              { Icon: BadgeCheck, k: "16+", v: "Years Experience" },
              { Icon: MapPin, k: String(COUNTIES.length), v: "GA Counties Served" },
              { Icon: Target, k: "24h", v: "Typical Response" },
            ].map(({ Icon, k, v, sub }, i) => (
              <div
                key={v}
                className={`p-6 flex items-center gap-4 border-slate-800 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""} ${i === 1 ? "md:border-r" : ""}`}
              >
                <Icon className="text-red-600 shrink-0" size={36} strokeWidth={1.5} />
                <div className="text-left">
                  <div className="font-impact text-white text-4xl leading-none">{k}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-red-500 font-bold mt-1">{v}</div>
                  {sub && <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-0.5">{sub}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section data-testid="trust-bar" className="border-b border-slate-900 bg-slate-950">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
          <img
            src="/trust-badges.png"
            alt="GAF National Circle Company · Owens Corning Platinum Preferred Contractor · Top 100 Roofing Contractors"
            className="w-full h-auto block"
          />
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">
                What JD inspects for
              </div>
              <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none tracking-tight">
                Storm damage your<br />insurance should cover.
              </h2>
              <p className="text-slate-400 text-lg mt-6 max-w-md">
                Most homeowners don't realize their roof already qualifies. JD knows exactly what
                adjusters look for — and what they miss.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800">
              {[
                { Icon: Wind, t: "Wind damage", d: "Lifted, creased, or missing shingles from straight-line winds or storms." },
                { Icon: CloudHail, t: "Hail damage", d: "Granule loss, bruising, and fractured mats from impact events." },
                { Icon: TreePine, t: "Tree / limb damage", d: "Punctures, impact damage, and structural concerns from fallen limbs." },
                { Icon: FileCheck, t: "Insurance claims", d: "JD walks the roof with your adjuster and documents the damage properly." },
              ].map(({ Icon, t, d }) => (
                <div key={t} className="bg-slate-950 p-8 hover:bg-slate-900 transition-colors">
                  <Icon className="text-red-600" size={28} />
                  <h3 className="font-display font-bold uppercase text-xl text-white mt-4">{t}</h3>
                  <p className="text-slate-400 text-sm mt-2 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className="py-24 md:py-32 border-y border-slate-900 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">How it works</div>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none tracking-tight mb-16 max-w-3xl">
            Three steps to a new roof — at little or no out-of-pocket cost.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-slate-800 border border-slate-800">
            {[
              { n: "01", Icon: Clock, t: "Schedule", d: "Call, text, or send the form. JD picks a time that works for you." },
              { n: "02", Icon: ShieldCheck, t: "Free inspection", d: "JD inspects the roof, takes photos, and documents storm damage." },
              { n: "03", Icon: Award, t: "Claim & install", d: "JD walks the roof with your adjuster and crews handle the replacement." },
            ].map(({ n, Icon, t, d }) => (
              <div key={n} className="bg-slate-950 p-10">
                <div className="flex items-baseline justify-between">
                  <span className="font-display font-black text-6xl text-slate-800">{n}</span>
                  <Icon className="text-red-600" size={28} />
                </div>
                <h3 className="font-display font-bold uppercase text-2xl text-white mt-6">{t}</h3>
                <p className="text-slate-400 mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSPECTION FORM */}
      <section id="inspect" data-testid="inspect-section" className="py-24 md:py-32 bg-slate-950 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">
                Schedule your inspection
              </div>
              <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none tracking-tight">
                Free.<br />No obligation.<br />No pressure.
              </h2>
              <p className="text-slate-300 mt-6 leading-relaxed text-lg max-w-md">
                Fastest way is to call or text JD directly. Or send the form and he'll reach out
                — usually within a few hours.
              </p>

              <div className="mt-10 space-y-4">
                <a
                  data-testid="contact-call"
                  href={`tel:${PHONE_TEL}`}
                  className="flex items-center gap-4 bg-slate-900 border border-slate-800 hover:border-red-600 transition-colors px-6 py-5 group"
                >
                  <Phone className="text-red-600" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400">Call</div>
                    <div className="font-display font-bold text-xl text-white group-hover:text-red-600">
                      {PHONE}
                    </div>
                  </div>
                </a>
                <a
                  data-testid="contact-text"
                  href={`sms:${PHONE_TEL}`}
                  className="flex items-center gap-4 bg-slate-900 border border-slate-800 hover:border-red-600 transition-colors px-6 py-5 group"
                >
                  <MessageSquare className="text-red-600" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-400">Text</div>
                    <div className="font-display font-bold text-xl text-white group-hover:text-red-600">
                      {PHONE}
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <div className="lg:col-span-7">
              <InspectionForm />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section data-testid="service-area" className="py-24 md:py-32 border-t border-slate-900 bg-slate-900/40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4 text-center">
            Where JD works
          </div>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none tracking-tight text-center">
            {COUNTIES.length} Georgia counties served
          </h2>
          <p className="text-slate-400 mt-5 text-center max-w-2xl mx-auto">
            Highlighted counties below are part of JD's regular inspection route across metro Atlanta
            and west/south Georgia.
          </p>

          <div className="mt-12 grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-3">
              <GeorgiaMap />
              <div className="mt-6 flex items-center justify-center gap-6 text-xs uppercase tracking-widest">
                <span className="inline-flex items-center gap-2 text-slate-300">
                  <span className="inline-block w-4 h-4 bg-red-600 border border-red-300" /> Serviced
                </span>
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <span className="inline-block w-4 h-4 bg-slate-800 border border-slate-600" /> Not in route
                </span>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">
                Counties on JD's route
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {[...COUNTIES].sort().map((c) => (
                  <div
                    key={c}
                    className="font-display font-bold uppercase text-base text-white border-l-2 border-red-600 pl-3 py-1"
                  >
                    {c}
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-sm mt-6">
                Not in one of these counties? Call or text JD — he may still be able to help.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SHINGLE TEASER */}
      <section data-testid="shingle-teaser" className="py-24 md:py-32 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-4">
              Premium GAF shingles
            </div>
            <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none tracking-tight">
              See the shingles you'll get to choose from.
            </h2>
            <p className="text-slate-400 mt-6 leading-relaxed text-lg max-w-md">
              GAF Timberline Natural Shadow, HDZ, and UHDZ lines — with industry-leading wind ratings
              and limited lifetime warranties.
            </p>
            <Link
              data-testid="see-shingles"
              to="/shingles"
              className="mt-8 inline-flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 px-8 py-4 transition-colors"
            >
              See shingle samples <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {SHINGLE_LINES.map((s, i) => (
              <div key={i} className="aspect-[3/4] bg-slate-900 overflow-hidden border border-slate-800">
                <img src={s.image} alt={s.alt || s.name} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEW CTA */}
      <section data-testid="review-cta" className="py-20 md:py-24 border-t border-slate-900 bg-slate-950">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-1 mb-5 text-red-600">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={22} fill="currentColor" />
            ))}
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 mb-3">
            Worked with JD before?
          </div>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl text-white tracking-tight">
            Leave a Google review.
          </h2>
          <p className="text-slate-400 mt-5 max-w-xl mx-auto leading-relaxed">
            Your review helps other homeowners trust JD with their storm damage claim — it takes
            less than 60 seconds.
          </p>
          <a
            data-testid="google-review-btn"
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 px-8 py-4 transition-colors"
          >
            <Star size={16} fill="currentColor" /> Leave a Google review
          </a>
        </div>
      </section>

      <Footer />
    </>
  );
}
